import AppHeader from '../../components/AppHeader';
import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Card,
  CardContent,
  CardActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel
} from '@mui/material';
import { ArrowBack, ArrowForward, Save, Add, Delete } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

// Auto-format Auftragsnummer
const formatAuftragsnummer = (value) => {
  const cleaned = value.replace(/[^\d/]/g, '');
  const parts = cleaned.split('/');
  
  if (parts.length === 1 && parts[0].length >= 4) {
    const digits = parts[0].slice(0, 4);
    const year = new Date().getFullYear().toString().slice(-2);
    return `${digits}/${year}`;
  }
  
  if (parts.length === 2) {
    const digits = parts[0].slice(0, 4);
    const yearPart = parts[1].slice(0, 2);
    return yearPart ? `${digits}/${yearPart}` : `${digits}/`;
  }
  
  return cleaned.slice(0, 7);
};

const capitalizeWords = (text) => {
  if (!text) return '';
  return text
    .split(/(\s+|-)/g)
    .map((part) => {
      if (part === ' ' || part === '-') return part;
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join('');
};

const steps = ['Kundendaten', 'Anlagendetails', 'Zusammenfassung'];

export default function IBNWPForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const customer = location.state?.customer;

  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    // Kundendaten
    kunde: customer?.name || '',
    kunde_strasse: customer?.strasse || '',
    kunde_plz: customer?.plz || '',
    kunde_ort: customer?.ort || '',
    auftrag: '',
    datum: new Date().toISOString().split('T')[0],
    anlagen_id: '',
    standort: '',
    montageort_aussengeraet: '',
    monteur: user?.full_name || user?.username || '',
    
    // Anlagendetails
    hersteller: '',
    hersteller_manuell: '',
    aussengeraet: {
      modell: '',
      seriennummer: ''
    },
    innengeraete: [
      { modell: '', seriennummer: '' }
    ],
    kondenswasserpumpen_vorhanden: false,
    kondenswasserpumpen: []
  });

  const handleChange = (field, value) => {
    if (field.startsWith('aussengeraet.')) {
      const subfield = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        aussengeraet: {
          ...prev.aussengeraet,
          [subfield]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  // Innengerät hinzufügen
  const addInnengeraet = () => {
    if (formData.innengeraete.length < 20) {
      setFormData(prev => ({
        ...prev,
        innengeraete: [...prev.innengeraete, { modell: '', seriennummer: '' }]
      }));
    }
  };

  // Innengerät entfernen
  const removeInnengeraet = (index) => {
    if (formData.innengeraete.length > 1) {
      setFormData(prev => ({
        ...prev,
        innengeraete: prev.innengeraete.filter((_, i) => i !== index)
      }));
    }
  };

  // Innengerät ändern
  const updateInnengeraet = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      innengeraete: prev.innengeraete.map((ig, i) => 
        i === index ? { ...ig, [field]: value } : ig
      )
    }));
  };

  // Kondenswasserpumpe hinzufügen
  const addKondenswasserpumpe = () => {
    if (formData.kondenswasserpumpen.length < 20) {
      setFormData(prev => ({
        ...prev,
        kondenswasserpumpen: [...prev.kondenswasserpumpen, { modell: '', seriennummer: '' }]
      }));
    }
  };

  // Kondenswasserpumpe entfernen
  const removeKondenswasserpumpe = (index) => {
    setFormData(prev => ({
      ...prev,
      kondenswasserpumpen: prev.kondenswasserpumpen.filter((_, i) => i !== index)
    }));
  };

  // Kondenswasserpumpe ändern
  const updateKondenswasserpumpe = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      kondenswasserpumpen: prev.kondenswasserpumpen.map((pump, i) => 
        i === index ? { ...pump, [field]: value } : pump
      )
    }));
  };

  const handleNext = () => {
    // Validation
    if (activeStep === 0) {
      if (!formData.auftrag) {
        setError('Auftragsnummer ist ein Pflichtfeld');
        return;
      }
      const pattern = /^\d{4}\/\d{2}$/;
      if (!pattern.test(formData.auftrag)) {
        setError('Auftragsnummer muss im Format "1234/24" sein');
        return;
      }
      if (!formData.kunde) {
        setError('Bitte Kundenname ausfüllen');
        return;
      }
    }

    if (activeStep === 1) {
      if (!formData.hersteller) {
        setError('Bitte Hersteller auswählen');
        return;
      }
      if (!formData.aussengeraet.modell) {
        setError('Bitte Außengerät-Modell eingeben');
        return;
      }
      // Prüfe ob mindestens ein Innengerät ausgefüllt ist
      const hasValidInnengeraet = formData.innengeraete.some(ig => ig.modell);
      if (!hasValidInnengeraet) {
        setError('Bitte mindestens ein Innengerät eingeben');
        return;
      }
    }

    setError('');
    if (activeStep < steps.length - 1) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setError('');
    if (activeStep > 0) {
      setActiveStep(prev => prev - 1);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Kundendaten
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Kundendaten
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Kunde *"
                  value={formData.kunde}
                  onChange={(e) => handleChange('kunde', capitalizeWords(e.target.value))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Straße"
                  value={formData.kunde_strasse}
                  onChange={(e) => handleChange('kunde_strasse', capitalizeWords(e.target.value))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="PLZ"
                  value={formData.kunde_plz}
                  onChange={(e) => handleChange('kunde_plz', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Ort"
                  value={formData.kunde_ort}
                  onChange={(e) => handleChange('kunde_ort', capitalizeWords(e.target.value))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Auftragsnummer *"
                  value={formData.auftrag}
                  onChange={(e) => {
                    const formatted = formatAuftragsnummer(e.target.value);
                    handleChange('auftrag', formatted);
                  }}
                  placeholder="1234/24"
                  helperText="Format: 1234/24 (4 Ziffern / 2-stelliges Jahr)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Datum"
                  type="date"
                  value={formData.datum}
                  onChange={(e) => handleChange('datum', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Anlagen-ID"
                  value={formData.anlagen_id}
                  onChange={(e) => handleChange('anlagen_id', e.target.value.toUpperCase())}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Standort/Gerätestandort"
                  value={formData.standort}
                  onChange={(e) => handleChange('standort', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Montageort Außengerät"
                  value={formData.montageort_aussengeraet}
                  onChange={(e) => handleChange('montageort_aussengeraet', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Monteur"
                  value={formData.monteur}
                  onChange={(e) => handleChange('monteur', e.target.value)}
                  helperText="Automatisch aus Benutzerprofil"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1: // Anlagendetails
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Anlagendetails
            </Typography>

            <Grid container spacing={3}>
              {/* Hersteller */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Hersteller *</InputLabel>
                  <Select
                    value={formData.hersteller === 'Sonstige' || ['Panasonic', 'Kaysun', 'Mitsubishi', 'Daikin', 'Rotex', 'Viessmann'].includes(formData.hersteller) ? formData.hersteller : 'Sonstige'}
                    onChange={(e) => {
                      if (e.target.value === 'Sonstige') {
                        handleChange('hersteller', '');
                      } else {
                        handleChange('hersteller', e.target.value);
                      }
                    }}
                    label="Hersteller *"
                  >
                    <MenuItem value="Panasonic">Panasonic</MenuItem>
                    <MenuItem value="Kaysun">Kaysun</MenuItem>
                    <MenuItem value="Mitsubishi">Mitsubishi</MenuItem>
                    <MenuItem value="Daikin">Daikin</MenuItem>
                    <MenuItem value="Rotex">Rotex</MenuItem>
                    <MenuItem value="Viessmann">Viessmann</MenuItem>
                    <MenuItem value="Sonstige">Sonstiger Hersteller</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {formData.hersteller === 'Sonstige' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Hersteller (manuell eingeben) *"
                    value={formData.hersteller_manuell}
                    onChange={(e) => {
                      handleChange('hersteller_manuell', e.target.value);
                      handleChange('hersteller', e.target.value);
                    }}
                    placeholder="Hersteller eingeben"
                  />
                </Grid>
              )}

              {/* Außengerät */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Außengerät
                  </Typography>
                </Divider>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Modell *"
                  value={formData.aussengeraet.modell}
                  onChange={(e) => handleChange('aussengeraet.modell', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Seriennummer"
                  value={formData.aussengeraet.seriennummer}
                  onChange={(e) => handleChange('aussengeraet.seriennummer', e.target.value)}
                />
              </Grid>

              {/* Innengeräte */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Innengeräte ({formData.innengeraete.length}/20)
                  </Typography>
                </Divider>
              </Grid>
              
              {formData.innengeraete.map((ig, index) => (
                <Grid item xs={12} key={index}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        Innengerät {index + 1}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Typ/Modell"
                            value={ig.modell}
                            onChange={(e) => updateInnengeraet(index, 'modell', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Seriennummer"
                            value={ig.seriennummer}
                            onChange={(e) => updateInnengeraet(index, 'seriennummer', e.target.value)}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                    <CardActions>
                      {formData.innengeraete.length > 1 && (
                        <Button
                          size="small"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => removeInnengeraet(index)}
                        >
                          Entfernen
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              ))}
              
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={addInnengeraet}
                  disabled={formData.innengeraete.length >= 20}
                >
                  + INNENGERÄT HINZUFÜGEN
                </Button>
              </Grid>

              {/* Kondenswasserpumpen */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Kondenswasserpumpen
                  </Typography>
                </Divider>
              </Grid>
              
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Kondenswasserpumpe vorhanden?</FormLabel>
                  <RadioGroup
                    row
                    value={formData.kondenswasserpumpen_vorhanden}
                    onChange={(e) => {
                      const value = e.target.value === 'true';
                      handleChange('kondenswasserpumpen_vorhanden', value);
                      if (value && formData.kondenswasserpumpen.length === 0) {
                        addKondenswasserpumpe();
                      }
                    }}
                  >
                    <FormControlLabel value="true" control={<Radio />} label="Ja" />
                    <FormControlLabel value="false" control={<Radio />} label="Nein" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {formData.kondenswasserpumpen_vorhanden && (
                <>
                  {formData.kondenswasserpumpen.map((pump, index) => (
                    <Grid item xs={12} key={index}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" gutterBottom>
                            Pumpe {index + 1}
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Modell"
                                value={pump.modell}
                                onChange={(e) => updateKondenswasserpumpe(index, 'modell', e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Seriennummer"
                                value={pump.seriennummer}
                                onChange={(e) => updateKondenswasserpumpe(index, 'seriennummer', e.target.value)}
                              />
                            </Grid>
                          </Grid>
                        </CardContent>
                        <CardActions>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<Delete />}
                            onClick={() => removeKondenswasserpumpe(index)}
                          >
                            Entfernen
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                  
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={addKondenswasserpumpe}
                      disabled={formData.kondenswasserpumpen.length >= 20}
                    >
                      + PUMPE HINZUFÜGEN
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        );

      case 2: // Zusammenfassung
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Zusammenfassung
            </Typography>
            <Alert severity="info">
              Vollständiges Formular wird in der nächsten Phase implementiert.
              Hier würden alle eingegebenen Daten zur Überprüfung angezeigt.
            </Alert>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1">
                <strong>Kunde:</strong> {formData.kunde}
              </Typography>
              <Typography variant="body1">
                <strong>Auftrag:</strong> {formData.auftrag}
              </Typography>
              <Typography variant="body1">
                <strong>Hersteller:</strong> {formData.hersteller}
              </Typography>
              <Typography variant="body1">
                <strong>Innengeräte:</strong> {formData.innengeraete.length}
              </Typography>
              <Typography variant="body1">
                <strong>Kondenswasserpumpen:</strong> {formData.kondenswasserpumpen.length}
              </Typography>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Inbetriebnahme Wärmepumpe
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {renderStepContent()}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              onClick={handleBack}
              disabled={activeStep === 0}
              startIcon={<ArrowBack />}
            >
              ZURÜCK
            </Button>
            
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={() => {
                  alert('Vollständiges Formular wird in Phase 2 implementiert!');
                }}
                endIcon={<Save />}
              >
                PROTOKOLL ERSTELLEN (Demo)
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ArrowForward />}
              >
                WEITER
              </Button>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
